import { composeProgress } from "apollo/utils";
import {
  CLINICAL_CONSULTATION_ACTION,
  SEARCH_ACTION_DELETE_FORM,
} from "core/consts";
import { getErrorMessage } from "core/model/utils/errors";
import {
  Auction,
  AuctionForm,
  ClinicalConsultation,
  DocsFormName,
  Patient,
} from "core/types";
import { useDeleteUnsharedFileByType } from "dsl/ecosystems/PatientFiles/useDeleteUnsharedFileByType";
import { useClinicalConsultationAction } from "dsl/hooks";
import useSearchAction from "dsl/hooks/useSearchAction";
import { FORM_NAME_TO_FILE_TYPE } from "./utils";

export function useDeleteConfirmation({
  auction,
  consultation,
  form,
  formName,
  patient,
}: {
  auction: Auction | undefined;
  consultation: ClinicalConsultation | undefined;
  formName: DocsFormName;
  form: AuctionForm | null | undefined;
  patient: Patient;
}) {
  const [deleteAuctionForm, deleteAuctionFormProgress] = useSearchAction({
    actionType: SEARCH_ACTION_DELETE_FORM,
  });

  const [deleteFormConsultation, deleteFormConsultationProgress] =
    useClinicalConsultationAction({
      actionType: CLINICAL_CONSULTATION_ACTION.DELETE_FORM,
    });

  // todo: remove when all files are synced with forms
  const { deleteUnsharedFile } = useDeleteUnsharedFileByType({
    auctionId: auction?.id ?? 0,
    recareFileType: FORM_NAME_TO_FILE_TYPE[formName],
  });

  const onDelete = async () => {
    try {
      if (consultation) {
        if (!form?.id || !form.form_type) {
          console.error("form id or form type is not defined");
          return;
        }
        await deleteFormConsultation({
          consultation,
          context: {
            form_type: form.form_type,
            id: form.id,
          },
          patient,
        });
        return;
      }
      if (!auction) {
        console.error("auction is not defined");
        return;
      }
      await deleteAuctionForm({
        auction,
        context: {
          form_type: FORM_NAME_TO_FILE_TYPE[formName],
        },
      });

      // Fire-and-forget: file sync is best-effort cleanup and should not block
      // the UI from resetting after form deletion. The todo above notes this
      // will be removed once all files are synced with forms.
      deleteUnsharedFile();
    } catch (err) {
      console.error(`error deleting ${formName} form: ${getErrorMessage(err)}`);
    }
  };

  return {
    deleteFormProgress: composeProgress([
      deleteAuctionFormProgress,
      deleteFormConsultationProgress,
    ]),
    onDelete,
  };
}
